import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/untypedClient';

interface FeatureFlag {
  id: string;
  feature_key: string;
  enabled: boolean;
  label: string;
  category: string;
}

interface FeatureFlagContextType {
  flags: FeatureFlag[];
  isFeatureEnabled: (key: string) => boolean;
  toggleFeature: (key: string, enabled: boolean) => Promise<void>;
  ensureLoaded: () => void;
  loading: boolean;
  /** True once the flag rows have actually been read, so callers can avoid
   *  rendering a flagged element that a loaded flag would then hide. */
  resolved: boolean;
}

const FeatureFlagContext = createContext<FeatureFlagContextType>({
  flags: [],
  isFeatureEnabled: () => true,
  toggleFeature: async () => {},
  ensureLoaded: () => {},
  loading: false,
  resolved: false,
});

export const useFeatureFlags = () => useContext(FeatureFlagContext);

const QUERY_KEY = ['feature_flags'] as const;

export const FeatureFlagProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  // Defer the fetch until first user interaction or an explicit ensureLoaded().
  const [shouldLoad, setShouldLoad] = useState(false);
  const armed = useRef(false);

  const ensureLoaded = useCallback(() => {
    if (!armed.current) {
      armed.current = true;
      setShouldLoad(true);
    }
  }, []);

  useEffect(() => {
    if (armed.current) return;
    const trigger = () => ensureLoaded();
    const opts = { once: true, passive: true } as AddEventListenerOptions;
    window.addEventListener('pointerdown', trigger, opts);
    window.addEventListener('keydown', trigger, opts);
    window.addEventListener('touchstart', trigger, opts);
    return () => {
      window.removeEventListener('pointerdown', trigger, opts);
      window.removeEventListener('keydown', trigger, opts);
      window.removeEventListener('touchstart', trigger, opts);
    };
  }, [ensureLoaded]);

  const { data: flags = [], isLoading, isSuccess } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const { data } = await supabase.from('feature_flags').select('*');
      return (data ?? []) as FeatureFlag[];
    },
    enabled: shouldLoad,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const isFeatureEnabled = useCallback(
    (key: string) => {
      if (flags.length === 0) return true; // default-enabled while not loaded
      const flag = flags.find((f) => f.feature_key === key);
      return flag ? flag.enabled : true;
    },
    [flags]
  );

  const toggleFeature = useCallback(
    async (key: string, enabled: boolean) => {
      await supabase
        .from('feature_flags')
        .update({ enabled, updated_at: new Date().toISOString() })
        .eq('feature_key', key);
      queryClient.setQueryData<FeatureFlag[]>(QUERY_KEY, (prev) =>
        (prev ?? []).map((f) => (f.feature_key === key ? { ...f, enabled } : f))
      );
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
    [queryClient]
  );

  return (
    <FeatureFlagContext.Provider
      value={{ flags, isFeatureEnabled, toggleFeature, ensureLoaded, loading: shouldLoad && isLoading, resolved: isSuccess }}
    >
      {children}
    </FeatureFlagContext.Provider>
  );
};
